import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { when } from 'lit/directives/when.js';
import { imagesBaseUrl } from '../../constants';
import { baseStyles } from '../../styles/base';
import { dlStyles } from '../../styles/dl';
import { avatar, logotype } from '../../svg';
import type { AuthorDetails, Contributor } from './entities';

@customElement('umb-market-author-details')
export class UmbMarketAuthorDetails extends LitElement {
  @property({ type: Object })
  authorDetails?: AuthorDetails;

  @property({ type: String })
  name!: string;

  @property({ type: Array })
  owners!: Array<string>;

  @property({ type: Boolean })
  isHQ?: boolean;

  @property({ type: Boolean })
  lookingForContributors?: boolean;

  @property({ type: Boolean })
  lookingForMaintainer?: boolean;

  #getAuthorImage() {
    if (this.isHQ) {
      return html`${logotype}`;
    }

    if (this.authorDetails?.imageUrl) {
      const imageUrl = this.authorDetails?.imageUrl.startsWith('http')
        ? this.authorDetails.imageUrl
        : `${imagesBaseUrl}${this.authorDetails?.imageUrl}`;

      return html`<umb-market-src-set-image src=${imageUrl} alt=${this.name} />`;
    }

    return html`${avatar}`;
  }

  #getAuthorDetailLinks() {
    let authorDetailLink;
    let authorPackagesLink;

    if (this.authorDetails?.url) {
      authorDetailLink = html` <a
        href=${ifDefined(this.authorDetails?.url)}
        target="_blank"
        >Read more about the developer</a
      >`;
    }

    const or = authorDetailLink ? ' or ' : '';
    const explore = authorDetailLink ? 'explore' : 'Explore';

    if (this.owners.length === 1) {
      authorPackagesLink = html`${or}<a href="/author/${this.owners[0]}"
          >${explore} more packages by ${this.owners[0]}</a
        >`;
    } else {
      const linkArray = this.owners.map((o, i) => {
        let separator = '';
        if (i === this.owners.length - 1) separator = ' or ';
        else if (i > 0) separator = ', ';

        return html`${separator}<a href="/author/${o}">${o}</a>`;
      });
      authorPackagesLink = html`${or} ${explore} more packages by ${linkArray}`;
    }

    return html`<p class="footnote">
      ${authorDetailLink}${authorPackagesLink}
    </p>`;
  }

  render() {
    return html`
      <div id="dinkus" class="${this.isHQ ? '--hq' : ''}">
        ${this.#getAuthorImage()}
        <h2>${this.name}</h2>
      </div>
      ${when(
        this.authorDetails?.description,
        () => html`<p>${this.authorDetails?.description}</p>`
      )}
      ${this.#getAuthorDetailLinks()}
      ${when(
        this.authorDetails?.contributors.length,
        () => html`
          <dl>
            <div>
              <dt>Co-authors</dt>
              <dd>
                <ul class="flex-list">
                  ${this.authorDetails?.contributors.map(
                    (contributor: Contributor) =>
                      html`<li>
                        <a href=${ifDefined(contributor.url)} target="_blank"
                          >${contributor.name}</a
                        >
                      </li>`
                  )}
                </ul>
              </dd>
            </div>
          </dl>
        `
      )}
    `;
  }

  static styles = [
    baseStyles,
    dlStyles,
    css`
      :host {
        display: block;
        --logo-fill: var(--uui-color-default);
        --avatar-size: 64px;
        border-radius: 22px;
        border: 1px solid var(--color-border);
        padding: var(--m-spacer);
      }

      @media (min-width: 767px) {
        :host {
          padding: var(--xl-spacer);
        }
      }

      #dinkus {
        display: flex;
        margin-bottom: var(--m-spacer);
        align-items: center;
      }

      #dinkus:only-child,
      p:last-of-type {
        margin-bottom: 0;
      }

      umb-market-src-set-image,
      svg {
        display: block;
        min-width: var(--avatar-size);
        height: var(--avatar-size);
        margin-right: var(--m-spacer);
        border-radius: 50%;
      }

      #dinkus:not(.--hq) svg {
        --fill: var(--uui-color-surface);
        background: var(--uui-color-default);
        padding: var(--xs-spacer);
      }

      h2 {
        font-size: 20px;
        font-weight: 700;
        word-break: break-word;
      }

      a {
        color: var(--uui-color-text);
      }
      a[href]:hover {
        color: var(--uui-color-default);
      }

      dl {
        margin-top: var(--xl-spacer);
      }

      .footnote,
      .footnote a {
        font-size: 14px;
        color: #6b6b6b;
      }
    `,
  ];
}
